import { useRef, useState } from "react";
import api from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Camera, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function AvatarUpload({ employee, size = "lg", editable = true, onUpdate }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const initials = (employee?.full_name || "U").split(" ").map(s => s[0]).slice(0, 2).join("");
  const sizeCls = { sm: "w-9 h-9", md: "w-14 h-14", lg: "w-20 h-20", xl: "w-28 h-28" }[size];
  const iconSize = { sm: "w-3 h-3", md: "w-4 h-4", lg: "w-4 h-4", xl: "w-5 h-5" }[size];

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 250 * 1024) {
      toast.error("Please select an image under 250KB");
      return;
    }
    setUploading(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          await api.post(`/employees/${employee.id}/avatar`, { data_url: reader.result });
          toast.success("Avatar updated");
          onUpdate?.();
        } catch (err) {
          toast.error(err.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
      };
      reader.readAsDataURL(file);
    } catch {
      setUploading(false); toast.error("Failed");
    }
  };

  const remove = async () => {
    try { await api.delete(`/employees/${employee.id}/avatar`); toast.success("Avatar removed"); onUpdate?.(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="relative group inline-block" data-testid="avatar-upload">
      <Avatar className={sizeCls}>
        {employee?.avatar_url && <AvatarImage src={employee.avatar_url} alt={employee.full_name}/>}
        <AvatarFallback className="bg-slate-900 text-white">{initials}</AvatarFallback>
      </Avatar>
      {editable && (
        <>
          <button
            onClick={pick}
            disabled={uploading}
            className="absolute bottom-0 right-0 p-1.5 rounded-full bg-slate-900 text-white border-2 border-white opacity-0 group-hover:opacity-100 transition shadow"
            data-testid="avatar-upload-btn"
            title="Change avatar"
          >
            <Camera className={iconSize}/>
          </button>
          {employee?.avatar_url && (
            <button
              onClick={remove}
              className="absolute -top-1 -right-1 p-1 rounded-full bg-red-600 text-white border-2 border-white opacity-0 group-hover:opacity-100 transition shadow"
              title="Remove"
            >
              <Trash2 className="w-3 h-3"/>
            </button>
          )}
          <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="avatar-file"/>
        </>
      )}
    </div>
  );
}
